'use client';

import clsx from 'clsx';
import { FC, useEffect, useRef, useState } from 'react';

import { Button } from '@/general/components/Button/Button';
import { Chip } from '@/general/components/Chip/Chip';
import { debounce } from '@/utils/debounce';

import { Chip as ChipItem } from './PlpFilterChips.type';

interface Props {
  chips: ChipItem[];
  prependTestId?: string;
}

const MORE_BUTTON_WIDTH = 90;

export const PlpFilterChips: FC<Props> = ({ chips, prependTestId = '' }) => {
  const isMounted = useRef<boolean>(true);
  const refRoot = useRef<HTMLDivElement>(null);
  const refSizingTrack = useRef<HTMLDivElement>(null);
  const [initialised, setInitialised] = useState<boolean>(false);
  const [showAll, setShowAll] = useState<boolean>(false);
  const [chipsTotal, setChipsTotal] = useState<number>(chips.length);
  const toggleShowAll = () => setShowAll(!showAll);
  const totalChipsHidden = chips.length - chipsTotal;
  const hasMoreToShow = chips.length - chipsTotal > 0;

  const onResizeCallback = () => {
    if (refRoot.current && refSizingTrack.current && isMounted.current) {
      setChipsTotal(getMaxElementsInTrack(refRoot.current, refSizingTrack.current) ?? chips.length);
    }
  };
  const debouncedResizeCallback = debounce(onResizeCallback, 100);

  useEffect(() => {
    isMounted.current = true;
    onResizeCallback();
    setInitialised(true);
    window.addEventListener('resize', debouncedResizeCallback);
    return () => {
      window.removeEventListener('resize', debouncedResizeCallback);
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    onResizeCallback();
  }, [chips]);

  if (chips.length === 0) {
    return null;
  }

  return (
    <div className="-my-1 overflow-hidden py-1" ref={refRoot}>
      <div
        className={clsx('flex w-fit gap-2 whitespace-nowrap', {
          'flex-wrap': initialised && showAll,
        })}
      >
        {chips.slice(0, showAll ? chips.length : chipsTotal).map(({ key, ...item }) => (
          <Chip {...item} key={key} testid={`${prependTestId}PlpFilterChips-chip`} />
        ))}
        {hasMoreToShow && (
          <Button
            onClick={toggleShowAll}
            variant="tertiary"
            size="small"
            data-testid={`${prependTestId}PlpFilterChips-displayToggle`}
          >
            {showAll ? 'Hide' : `+${totalChipsHidden} more`}
          </Button>
        )}
      </div>
      {/* Element used to size the chips */}
      <div className="flex h-0 w-fit gap-2 overflow-hidden whitespace-nowrap" ref={refSizingTrack}>
        {chips.map(({ key, ...item }) => (
          <Chip {...item} key={key} disabled />
        ))}
      </div>
    </div>
  );
};

const getMaxElementsInTrack = (root: HTMLDivElement, track: HTMLDivElement) => {
  if (globalThis.window !== undefined) {
    // Get the total track size available
    const totalTrackSize = root.offsetWidth;
    const totalTrackSizeWithButton = totalTrackSize - MORE_BUTTON_WIDTH;
    // Loop through each of the child elements and compare them with the track size
    const children = track.querySelectorAll(':scope > *');

    const { length } = children;
    // Going by last children first as it's be quicker to find the split most of the time
    for (let i = length - 1; i >= 0; i -= 1) {
      const child = children[i] as HTMLElement;
      // Check if the element is outside of the total track size
      const currentLimit = child.offsetLeft + child.offsetWidth - track.offsetLeft;

      if (i === length - 1 && currentLimit < totalTrackSize) {
        return length;
      }
      if (currentLimit < totalTrackSizeWithButton) {
        return i + 1;
      }
    }
    return 0;
  }
  return null;
};
